import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import apiServices from '../../services/service';
import { Link, useNavigate } from 'react-router-dom';
import PipelineDealsByStage from './pipelineDealsByStage';
import { DateFormatter } from '../../shared/controls/formatters';
import { getAttendanceByUserId, getUser, getUserInfo, getUserInformation, getUserList } from '../../redux/selector';
import { toast } from 'react-toastify';
import { fetchDataAndUpdateStore } from '../../redux/actions';
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { tr } from 'date-fns/locale';

// GrowthOS-style KPI card pieces (presentational only).
// NOTE: `spark` and `delta` are decorative placeholders — the count APIs
// (getTotalLead / getOpenOpportunity / getTotalOpenTicket) return no trend
// history, so these are not real data. Wire to a trend endpoint or remove.
const GosSparkline = ({ data, color, gid }) => {
  const w = 76;
  const h = 30;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const rng = max - min || 1;
  const step = w / (data.length - 1);
  const pts = data.map((v, i) => [i * step, h - ((v - min) / rng) * (h - 4) - 2]);
  const line = pts
    .map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1))
    .join(' ');
  const area = `${line} L${w} ${h} L0 ${h} Z`;
  return (
    <svg
      className="gos-kpi-spark"
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const GosKpiCard = ({ tint, iconPath, label, value, spark, delta, sparkId }) => (
  <div className="grid-box gos-kpi">
    <div className="gos-kpi-top">
      <div className="gos-kpi-icon" style={{ background: tint.bg, color: tint.fg }}>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d={iconPath} />
        </svg>
      </div>
      <GosSparkline data={spark} color={tint.fg} gid={sparkId} />
    </div>
    <div className="gos-kpi-label">{label}</div>
    <div className="gos-kpi-val">{value}</div>
    {delta && (
      <div className={`gos-kpi-delta ${delta.up ? 'gos-delta-up' : 'gos-delta-down'}`}>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d={delta.up ? 'M12 19V5M5 12l7-7 7 7' : 'M12 5v14M5 12l7 7 7-7'} />
        </svg>
        {delta.text}
        <span className="gos-kpi-delta-sub">vs last month</span>
      </div>
    )}
  </div>
);

// ---- GrowthOS dashboard graph cards ----
// Pipeline Overview uses REAL data (opportunityStage). The donut (Lead Sources),
// area chart (Revenue) and Recent Activity are DUMMY placeholders matching the
// design — there is no backend source for them yet.
const GOS_PIPE_COLORS = ['#b34480', '#2e63a6', '#9a751c', '#6a4fae', '#2c7a45', '#c0473f', '#54738f', '#c96342'];

const DUMMY_SOURCE_MIX = [
  { name: 'Inbound', value: 34, color: 'var(--gos-accent)' },
  { name: 'LinkedIn Ads', value: 24, color: '#2e63a6' },
  { name: 'Referral', value: 18, color: '#9a751c' },
  { name: 'Webinar', value: 14, color: '#6a4fae' },
  { name: 'Cold Outreach', value: 10, color: '#b34480' },
];

const DUMMY_REVENUE = [
  { m: 'Jul', v: 62 }, { m: 'Aug', v: 58 }, { m: 'Sep', v: 71 }, { m: 'Oct', v: 69 },
  { m: 'Nov', v: 84 }, { m: 'Dec', v: 92 }, { m: 'Jan', v: 88 }, { m: 'Feb', v: 103 },
  { m: 'Mar', v: 97 }, { m: 'Apr', v: 118 }, { m: 'May', v: 126 }, { m: 'Jun', v: 141 },
];

const DUMMY_ACTIVITY = [
  { who: 'Anil Rao', action: 'moved', target: 'Lumen Retail', detail: 'to Negotiation', time: '8 min ago', color: 'var(--gos-accent)', init: 'AR' },
  { who: 'Sara Lin', action: 'closed', target: 'Cobalt Systems', detail: '— $96K Won', time: '1 hour ago', color: '#2c7a45', init: 'SL' },
  { who: 'GrowthOS', action: 'captured', target: 'Arc Studio', detail: 'via Cold Outreach', time: '3 hours ago', color: '#9a751c', init: 'GO' },
  { who: 'Sara Lin', action: 'sent proposal to', target: 'Tessellate', detail: '', time: '5 hours ago', color: '#2e63a6', init: 'SL' },
];

const GosDonut = ({ data, size = 130 }) => {
  const r = size / 2 - 8;
  const cx = size / 2;
  const cy = size / 2;
  const sw = 16;
  const C = 2 * Math.PI * r;
  const total = data.reduce((a, b) => a + b.value, 0);
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--gos-surface-3)" strokeWidth={sw} />
      {data.map((d, i) => {
        const frac = d.value / total;
        const dash = frac * C;
        const off = -acc * C;
        acc += frac;
        return (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={d.color}
            strokeWidth={sw}
            strokeDasharray={`${dash} ${C - dash}`}
            strokeDashoffset={off}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        );
      })}
      <text x={cx} y={cy - 2} textAnchor="middle" fontSize="22" fontWeight="700" fill="var(--gos-text)">
        {total}%
      </text>
      <text x={cx} y={cy + 16} textAnchor="middle" fontSize="10.5" fill="var(--gos-text-3)">
        of leads
      </text>
    </svg>
  );
};

const GosAreaChart = ({ data, color = 'var(--gos-accent)' }) => {
  const w = 560;
  const h = 230;
  const padL = 40;
  const padB = 26;
  const padT = 14;
  const padR = 12;
  const cw = w - padL - padR;
  const ch = h - padB - padT;
  const vals = data.map((d) => d.v);
  const max = Math.ceil(Math.max(...vals) / 20) * 20;
  const min = 0;
  const slot = cw / data.length;
  const barW = Math.min(38, slot * 0.6);
  const xc = (i) => padL + slot * i + slot / 2;
  const y = (v) => padT + ch - ((v - min) / (max - min)) * ch;
  const grids = [0, 0.25, 0.5, 0.75, 1];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{ display: 'block', height: 'auto' }}>
      <defs>
        <linearGradient id="gosAreaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.95" />
          <stop offset="100%" stopColor={color} stopOpacity="0.55" />
        </linearGradient>
      </defs>
      {grids.map((g, i) => {
        const yy = padT + ch * g;
        return (
          <g key={i}>
            <line x1={padL} y1={yy} x2={w - padR} y2={yy} stroke="var(--gos-border)" strokeWidth="1" strokeDasharray={i === grids.length - 1 ? '0' : '2 5'} />
            <text x={padL - 10} y={yy + 3.5} textAnchor="end" fontSize="11" fontWeight="500" fill="var(--gos-text-3)">
              {'$' + Math.round(max * (1 - g)) + 'K'}
            </text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const bh = padT + ch - y(d.v);
        return <rect key={'b' + i} x={xc(i) - barW / 2} y={y(d.v)} width={barW} height={Math.max(0, bh)} rx="5" fill="url(#gosAreaGrad)" />;
      })}
      {data.map((d, i) => (
        <text key={'t' + i} x={xc(i)} y={h - 8} textAnchor="middle" fontSize="11" fontWeight="500" fill="var(--gos-text-3)">
          {d.m}
        </text>
      ))}
    </svg>
  );
};

const GosPipeline = ({ stages }) => (
  <div className="gos-pipe">
    {stages.map((s, i) => (
      <div className="gos-pipe-stage" key={i}>
        <div className="gos-pipe-bar" style={{ background: s.color }}></div>
        <div className="gos-pipe-name">{s.name}</div>
        <div className="gos-pipe-count">{s.count}</div>
      </div>
    ))}
  </div>
);

// ---- Recent Tickets + Recent Open Opportunities (GrowthOS design, real data) ----
const gosPrioDot = (p) => {
  const s = String(p || '').toLowerCase();
  if (s.includes('high') || s.includes('urgent') || s.includes('critical')) return 'gos-dot-rose';
  if (s.includes('med')) return 'gos-dot-amber';
  if (s.includes('low')) return 'gos-dot-blue';
  return 'gos-dot-gray';
};

const gosStatusBadge = (s) => {
  const v = String(s || '').toLowerCase();
  if (v.includes('progress')) return 'gos-b-amber';
  if (v.includes('wait') || v.includes('hold') || v.includes('pending')) return 'gos-b-gray';
  if (v.includes('resolv') || v.includes('closed') || v.includes('complete') || v.includes('done')) return 'gos-b-green';
  if (v.includes('open') || v.includes('new')) return 'gos-b-blue';
  if (v.includes('reopen') || v.includes('escalat')) return 'gos-b-rose';
  return 'gos-b-gray';
};

const gosStageOf = (o) =>
  o.stageName || o.stage || o.salesStage || o.opportunityStage || o.dealStage || '';

const gosStageBadge = (stage) => {
  const s = String(stage || '').toLowerCase();
  if (s.includes('won') || s.includes('complete')) return 'gos-b-green';
  if (s.includes('lost')) return 'gos-b-red';
  if (s.includes('negoti') || s.includes('review')) return 'gos-b-purple';
  if (s.includes('propos') || s.includes('quote') || s.includes('price')) return 'gos-b-amber';
  if (s.includes('qualif') || s.includes('demo') || s.includes('analysis') || s.includes('nda') || s.includes('msa')) return 'gos-b-blue';
  if (s.includes('new') || s.includes('prospect') || s.includes('lead') || s.includes('identif')) return 'gos-b-rose';
  return 'gos-b-gray';
};

const gosOppValue = (o) => {
  const v = o.amount ?? o.expectedRevenue ?? o.value ?? o.dealValue ?? o.opportunityAmount ?? o.estimatedValue;
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  if (n >= 1000000) return '$' + (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1000) return '$' + Math.round(n / 1000) + 'K';
  return '$' + n;
};

const GosRecentTickets = ({ weekList = [], todayList = [], tab, setTab, loading }) => {
  const rows = tab === 'today' ? todayList : weekList;
  return (
    <div className="card gos-card gos-flexcard">
      <div className="gos-card-head">
        <div className="gos-card-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V7ZM14 5v14" />
          </svg>
          Recent Tickets
        </div>
        <div className="gos-seg">
          <button className={'gos-seg-btn' + (tab !== 'today' ? ' on' : '')} onClick={() => setTab('week')}>
            This Week <span className="gos-seg-n">{weekList.length}</span>
          </button>
          <button className={'gos-seg-btn' + (tab === 'today' ? ' on' : '')} onClick={() => setTab('today')}>
            Today <span className="gos-seg-n">{todayList.length}</span>
          </button>
        </div>
      </div>
      <div className="gos-ticket-list">
        {loading ? (
          <div className="gos-tbl-empty">Loading…</div>
        ) : rows && rows.length > 0 ? (
          rows.map((t, i) => (
            <div className="gos-ticket-row" key={i}>
              <span className={'gos-ticket-dot ' + gosPrioDot(t.priority)}></span>
              <div className="gos-ticket-main">
                <div className="gos-ticket-subj">{t.subject}</div>
                <div className="gos-ticket-meta">
                  {t.description ? t.description + ' · ' : ''}
                  {DateFormatter(t.dueDate)}
                </div>
              </div>
              {t.status ? <span className={'gos-badge ' + gosStatusBadge(t.status)}>{t.status}</span> : null}
            </div>
          ))
        ) : (
          <div className="gos-tbl-empty">No tickets available</div>
        )}
      </div>
    </div>
  );
};

const GosRecentOpps = ({ opps = [], loading }) => (
  <div className="card gos-card gos-flexcard">
    <div className="gos-card-head">
      <div className="gos-card-title">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM12 12h.01" />
        </svg>
        Recent Open Opportunities
      </div>
      <Link to={`/opportunity`} className="gos-link">View all</Link>
    </div>
    <div className="gos-opp-table">
      <table className="gos-tbl">
        <thead>
          <tr>
            <th>Opportunity</th>
            <th>Account</th>
            <th>Stage</th>
            <th style={{ textAlign: 'right' }}>Value</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan="4" className="gos-tbl-empty">Loading…</td></tr>
          ) : opps && opps.length > 0 ? (
            opps.map((o, i) => {
              const stage = gosStageOf(o);
              return (
                <tr key={i}>
                  <td><span className="gos-cell-name">{o.name}</span></td>
                  <td className="gos-muted">{o.accountName}</td>
                  <td>
                    {stage ? (
                      <span className={'gos-badge ' + gosStageBadge(stage)}>
                        <span className="gos-pip"></span>
                        {stage}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{gosOppValue(o)}</td>
                </tr>
              );
            })
          ) : (
            <tr><td colSpan="4" className="gos-tbl-empty">No opportunities available</td></tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const ConfirmCheckOutModal = ({ show, onConfirm, onCancel }) => {
  return (
    // <div style={{ height: '100%' }}>
    <Modal
      show={show}
      onHide={onCancel}
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName="confirm-checkout-modal"
      animation={true}
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="modal-title-custom">
          Confirm Checkout
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="modal-body-custom">
        <div className="modal-icon-wrapper">
          <svg
            className="modal-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <p className="modal-message">
          Are you sure you want to checkout?
        </p>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button
          variant="secondary"
          onClick={onCancel}
          className="modal-btn modal-btn-cancel"
        >
          No
        </Button>

        <Button
          variant="danger"
          onClick={onConfirm}
          className="modal-btn modal-btn-confirm"
        >
          Yes, Checkout
        </Button>
      </Modal.Footer>
    </Modal>
    // </div>
  );
};

// Dashboard view switcher — menu styling ported from the claude.ai/design
// "growthos" project (Dashboard.jsx → DashboardSwitch), populated with the
// app's real dashboards. 'standard' is the current page; 'pnc' navigates away.
const GOS_DASH_VIEWS = [
  { id: 'standard', label: 'Standard Dashboard', sub: 'Pipeline, tickets & opportunities', to: '/dashboard' },
  { id: 'pnc', label: 'P&C Dashboard', sub: 'Quotes, KPIs & workbook', to: '/pncQuote/dashboard' },
];

const GosDashboardSwitch = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const sel = 'standard';
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const cur = GOS_DASH_VIEWS.find((v) => v.id === sel);
  return (
    <div className={'gos-dash-switch' + (open ? ' open' : '')} ref={ref}>
      <button type="button" className="gos-btn gos-btn-ghost" onClick={() => setOpen((o) => !o)}>
        <span className="gos-ds-dot"></span>
        {cur.label}
        <svg className="gos-caret" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="gos-dash-menu">
          {GOS_DASH_VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              className={'gos-dash-menu-item' + (v.id === sel ? ' sel' : '')}
              onClick={() => {
                setOpen(false);
                if (v.id !== sel) navigate(v.to);
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div>{v.label}</div>
                <div className="gos-di-sub">{v.sub}</div>
              </div>
              {v.id === sel && (
                <svg className="gos-di-check" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- Dashboard skeleton (shimmer placeholders shown while data loads) ----
// Mirrors the real layout containers (KPI row, graph cards, recent-data cards)
// so spacing matches; reuses the global .gos-skel-* shimmer atoms.
const DashboardSkeleton = () => (
  <>
    <div className="flex_boxes">
      <div className="grid-container-box">
        {Array.from({ length: 4 }).map((_, i) => (
          <div className="grid-box gos-kpi" key={i}>
            <div className="gos-kpi-top">
              <span className="gos-skel-box gos-skel-an" style={{ width: 42, height: 42, borderRadius: 12 }} />
              <span className="gos-skel-bar gos-skel-an" style={{ width: 76, height: 30, borderRadius: 8 }} />
            </div>
            <span className="gos-skel-bar gos-skel-an sm" style={{ width: '52%', marginTop: 16 }} />
            <span className="gos-skel-bar gos-skel-an" style={{ width: '38%', height: 22, marginTop: 12 }} />
          </div>
        ))}
      </div>
    </div>
    <div className="gos-graphs">
      <div className="gos-dash-2col">
        {Array.from({ length: 2 }).map((_, i) => (
          <div className="card gos-card" key={i}>
            <div className="gos-card-head">
              <span className="gos-skel-bar gos-skel-an" style={{ width: 150, height: 13 }} />
              <span className="gos-skel-bar gos-skel-an sm" style={{ width: 70 }} />
            </div>
            <div className="gos-card-pad gos-skel-graph">
              {Array.from({ length: 5 }).map((_, j) => (
                <span className="gos-skel-bar gos-skel-an" key={j} style={{ width: `${82 - j * 12}%` }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="gos-grid2">
      {Array.from({ length: 2 }).map((_, i) => (
        <div className="card gos-card gos-flexcard" key={i}>
          <div className="gos-card-head">
            <span className="gos-skel-bar gos-skel-an" style={{ width: 160, height: 13 }} />
            <span className="gos-skel-bar gos-skel-an sm" style={{ width: 80 }} />
          </div>
          <div className="gos-skel-list">
            {Array.from({ length: 5 }).map((_, j) => (
              <div className="gos-skel-list-row" key={j}>
                <span className="gos-skel-avatar gos-skel-an" style={{ width: 30, height: 30, borderRadius: 8 }} />
                <div className="gos-skel-lines" style={{ flex: 1 }}>
                  <span className="gos-skel-bar gos-skel-an" style={{ width: '68%' }} />
                  <span className="gos-skel-bar gos-skel-an sm" style={{ width: '44%' }} />
                </div>
                <span className="gos-skel-bar gos-skel-an" style={{ width: 58, height: 20, borderRadius: 6 }} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </>
);

const HomeCRM = () => {
  const stateData = useSelector((state) => state);
  const [leadCount, setLeadCount] = useState();
  const [totalOpenTicket, setTotalOpenTicket] = useState();
  const [openOpportunity, setOpenOpportunity] = useState();
  const [weekTicketList, setWeekTicketList] = useState([]);
  const [todayTicketList, setTodayTicketList] = useState([]);
  const [opportunityList, setOpportunityList] = useState([]);
  const [pipelineData, setPipelineData] = useState([]);
  const [isShowsLoader, setIsShowsLoader] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const userInfo = useSelector(getUserInformation);
  const dispatch = useDispatch();
  const userListData = useSelector(getUserList);
  useEffect(() => {
    if (!userListData || userListData.length === 0) {
      dispatch(fetchDataAndUpdateStore({ key: 'userList' }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [buttonLabel, setButtonLabel] = useState("Checkin");
  const [showCheckoutConfirm, setShowCheckoutConfirm] = useState(false);
  const AttendanceFlag = process.env.REACT_APP_ATTENDANCE_FLAG === "true";

  // useEffect(() => {
  //   const isDismissed = sessionStorage.getItem('downloadBannerDismissed');
  //   if (isDismissed) {
  //     setIsVisible(false);
  //   }
  // }, []);

  const handleDownload = () => {
    setIsAnimating(true);
    // Wait for animation to complete before hiding
    setTimeout(() => {
      setIsVisible(false);
      setIsAnimating(false);
    }, 400); // Match animation duration
    sessionStorage.setItem('downloadBannerDismissed', 'true');
  };

  // Auto-hide the "Get our mobile app" banner after a few seconds.
  useEffect(() => {
    const BANNER_VISIBLE_MS = 6000; // how long the banner stays before sliding up
    const slideUp = setTimeout(() => setIsAnimating(true), BANNER_VISIBLE_MS);
    const remove = setTimeout(() => {
      setIsVisible(false);
      setIsAnimating(false);
    }, BANNER_VISIBLE_MS + 400); // +400ms = slide-up animation duration
    return () => {
      clearTimeout(slideUp);
      clearTimeout(remove);
    };
  }, []);

  let storeLoading = isLoadingByKey(
    stateData,
    'revenueTypeList',
    'leadSourceList',
    'industryList',
    'accountType'
  );
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
  const [loading, setLoading] = useState(storeLoading);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useEffect(() => {
    setLoading(true);
    apiServices
      .getTotalLead()
      .then((response) => {
        if (response.status === 200) {
          const leadCount = response.data;
          setLeadCount(leadCount);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error('Error', error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    setLoading(true);
    apiServices
      .getTotalOpenTicket()
      .then((response) => {
        if (response.status === 200) {
          const ticketCount = response.data;
          setTotalOpenTicket(ticketCount);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error('Error', error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    setLoading(true);
    apiServices
      .getOpenOpportunity()
      .then((response) => {
        if (response.status === 200) {
          const opportunityCount = response.data;
          setOpenOpportunity(opportunityCount);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error('Error', error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    setLoading(true);
    setIsShowsLoader(true);
    apiServices
      .getCrmDashboard()
      .then((response) => {
        if (response.status === 200) {
          const weekTicketList = response.data.ticketthisweek;
          const todayTicketList = response.data.ticketToday;
          const opportunityList = response.data.opportunity;
          setWeekTicketList(weekTicketList);
          setTodayTicketList(todayTicketList);
          setOpportunityList(opportunityList);
          setPipelineData(response?.data?.opportunityStage);
          setLoading(false);
          setIsShowsLoader(false);
        }
      })
      .catch((error) => {
        console.error('Error', error);
        setLoading(false);
      });
  }, []);

  const bannerStyles = `
  @keyframes slideDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes slideUp {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(-100%);
      opacity: 0;
    }
  }

  .banner-enter {
    animation: slideDown 0.4s ease-out forwards;
  }

  .banner-exit {
    animation: slideUp 0.4s ease-out forwards;
  }
`;

  const attendanceByUserId = useSelector(getAttendanceByUserId);
  useEffect(() => {
    if (!attendanceByUserId && userInfo) {
      dispatch(
        fetchDataAndUpdateStore({
          key: "attendanceByUserId",
          inputData: userInfo?.userId,
        })
      );
    }
  }, [userInfo]);


  const getTodayAttendance = () => {
    const list = attendanceByUserId?.userAttendance || [];
    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD

    return list.find((item) => {
      if (!item?.checkIn) return false;
      return item.checkIn.split("T")[0] === today;
    });
  };


  useEffect(() => {
    const todayRecord = getTodayAttendance();
    if (todayRecord?.checkIn && !todayRecord?.checkOut) {
      setButtonLabel("Checkout");
      setIsCheckedIn(true);
    } else {
      setButtonLabel("Checkin");
      setIsCheckedIn(false);
    }
  }, [attendanceByUserId]);

  const handleCheckinCheckout = async () => {
    const todayRecord = getTodayAttendance();

    // 👉 if already checked in but not checked out → ask confirmation
    if (todayRecord?.checkIn && !todayRecord?.checkOut) {
      setShowCheckoutConfirm(true);
      return;
    }

    // otherwise normal checkin
    await processAttendance();
  };

  const processAttendance = async () => {
    try {
      setLoading(true);

      const currentTime = new Date().toISOString();
      const todayRecord = getTodayAttendance();

      let payload = {
        userId: userInfo?.userId,
        employeeCode: userInfo?.empId,
        status: "Present",
        checkIn: null,
        checkOut: null,
        duration: 0,
      };

      if (!todayRecord) {
        payload.checkIn = currentTime;
      }
      else if (!todayRecord?.checkIn) {
        payload.checkIn = currentTime;
      }
      else if (todayRecord?.checkIn && !todayRecord?.checkOut) {
        payload.checkIn = todayRecord.checkIn;
        payload.checkOut = currentTime;
      }
      else if (todayRecord?.checkIn && todayRecord?.checkOut) {
        toast.info("You already checked out for today");
        setLoading(false);
        return;
      }
      setLoading(true);
      const response = await apiServices.createAttendance(payload);

      if (response.status === 200 || response.status === 204) {
        dispatch(
          fetchDataAndUpdateStore({
            key: "attendanceByUserId",
            inputData: userInfo?.userId,
          })
        );

        toast.success(
          payload.checkOut
            ? "Checked-out successfully!"
            : "Checked-in successfully!"
        );
        setLoading(false);
      } else {
        handleApiErrors(response);
        setLoading(false);
      }
    } catch (error) {
      handleCatchErrors(error);
    } finally {
      setLoading(false);
      setShowCheckoutConfirm(false);
    }
  };





  // const handleCheckinCheckout = async () => {
  //   try {
  //     setLoading(true);

  //     const currentTime = new Date().toISOString();
  //     const todayRecord = getTodayAttendance();

  //     let payload = {
  //       userId: userInfo?.userId,
  //       employeeCode: userInfo?.empId,
  //       status: "Present",
  //       checkIn: null,
  //       checkOut: null,
  //       duration: 0,
  //     };

  //     // 🔵 CASE 1: No record for today → fresh checkin
  //     if (!todayRecord) {
  //       payload.checkIn = currentTime;
  //       payload.checkOut = null;
  //     }

  //     // 🔵 CASE 2: Today record exists but no checkin yet
  //     else if (!todayRecord?.checkIn) {
  //       payload.checkIn = currentTime;
  //       payload.checkOut = null;
  //     }

  //     // 🔵 CASE 3: Checkin exists but checkout not done → DO CHECKOUT
  //     else if (todayRecord?.checkIn && !todayRecord?.checkOut) {
  //       payload.checkIn = todayRecord.checkIn; // send same checkin
  //       payload.checkOut = currentTime;
  //     }

  //     // 🔵 CASE 4: Already checked out today → optional block
  //     else if (todayRecord?.checkIn && todayRecord?.checkOut) {
  //       setLoading(false);
  //       toast.info("You already checked out for today");
  //       return;
  //     }

  //     const response = await apiServices.createAttendance(payload);

  //     if (response.status === 200 || response.status === 204) {
  //       dispatch(
  //         fetchDataAndUpdateStore({
  //           key: "attendanceByUserId",
  //           inputData: userInfo?.userId,
  //         })
  //       );

  //       toast.success(
  //         payload.checkOut ? "Checked-out successfully!" : "Checked-in successfully!"
  //       );
  //     } else {
  //       handleApiErrors(response);
  //     }
  //   } catch (error) {
  //     handleCatchErrors(error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleApiErrors = (response) => {
    const errorMessages = [];

    if (response?.errors) {
      for (const key in response.errors) {
        errorMessages.push(...response.errors[key]);
      }
    }

    if (errorMessages.length) {
      errorMessages.forEach((msg) =>
        toast.error(msg, { position: "top-right" })
      );
    } else {
      toast.error("Something went wrong");
    }
  };

  const handleCatchErrors = (error) => {
    if (error?.response?.data?.errors) {
      const errors = error.response.data.errors;
      Object.keys(errors).forEach((key) => {
        errors[key].forEach((msg) => toast.error(msg));
      });
    } else {
      toast.error("Server error occurred");
    }
  };



  const [ticketTab, setTicketTab] = useState('week');
  const greetingHour = new Date().getHours();
  const greeting =
    greetingHour < 12 ? 'Good morning' : greetingHour < 18 ? 'Good afternoon' : 'Good evening';

  // real pipeline (opportunityStage) mapped to the GrowthOS "Pipeline Overview" card
  const pipelineStages = [...pipelineData]
    .sort((a, b) => a.sequence - b.sequence)
    .map((s, i) => ({
      name: s.stageName,
      count: s.stageCount,
      color: GOS_PIPE_COLORS[i % GOS_PIPE_COLORS.length],
    }));

  return (
    <>
      <style>{bannerStyles}</style>
      {isVisible && (
        <div
          className={`position-fixed top-0 start-0 end-0 bg-primary shadow ${isAnimating ? 'banner-exit' : 'banner-enter'
            }`}
          style={{ zIndex: 99999 }}
        >
          <div className="container-fluid">
            <div className="d-flex align-items-center justify-content-between py-3 px-3">
              <div className="d-flex align-items-center flex-grow-1">
                <svg
                  className="me-3 text-white"
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-white fw-semibold">
                  Get our mobile app for the best experience!
                </span>
              </div>

              <div className="d-flex align-items-center gap-2">
                <Link
                  href="/assets/leadTrack.apk"
                  download="leadTrack.apk"
                  onClick={handleDownload}
                  className="btn btn-light btn-sm fw-semibold text-nowrap"
                >
                  Download APK
                </Link>

                <button
                  onClick={handleDownload}
                  className="btn btn-link text-white p-1"
                  aria-label="Close banner"
                  style={{ textDecoration: 'none' }}
                >
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="inner-page" style={containerStyle}>
        <div className="home-content">
          <div className="gos-page-head">
            <div>
              <div className="gos-page-title">
                {greeting}
                {userInfo?.display_name ? `, ${userInfo.display_name}` : ''}
              </div>
              <div className="gos-page-sub">
                Here's what's happening across your workspace today
              </div>
            </div>
            <div className="gos-page-actions">
              <GosDashboardSwitch />

              <button type="button" className="gos-btn gos-btn-ghost">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 2v4M16 2v4M3 9h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
                </svg>
                Last 30 days
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              <Link to="/leads/CreateNew" className="gos-btn gos-btn-primary">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                New Lead
              </Link>
            </div>
          </div>
          {loading ? (
            <DashboardSkeleton />
          ) : (
          <>
          <div className="flex_boxes">
            <div className="grid-container-box">
              <GosKpiCard
                tint={{ bg: 'var(--gos-rose-bg)', fg: 'var(--gos-rose-fg)' }}
                iconPath="M12 3l2.4 6.6L21 12l-6.6 2.4L12 21l-2.4-6.6L3 12l6.6-2.4L12 3Z"
                label="Total Leads"
                value={leadCount}
                spark={[12, 18, 14, 22, 19, 26, 24, 31]}
                delta={{ up: true, text: '+12.4%' }}
                sparkId="gosSpark1"
              />

              <GosKpiCard
                tint={{ bg: 'var(--gos-green-bg)', fg: 'var(--gos-green-fg)' }}
                iconPath="M3 17l6-6 4 4 8-8M21 7v5h-5"
                label="Open Opportunities"
                value={openOpportunity}
                spark={[40, 38, 45, 43, 52, 49, 58, 61]}
                delta={{ up: true, text: '+8.1%' }}
                sparkId="gosSpark2"
              />

              <GosKpiCard
                tint={{ bg: 'var(--gos-blue-bg)', fg: 'var(--gos-blue-fg)' }}
                iconPath="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM12 12h.01"
                label="Open Tickets"
                value={totalOpenTicket}
                spark={[20, 24, 22, 28, 26, 30, 29, 33]}
                delta={{ up: true, text: '+3.2%' }}
                sparkId="gosSpark3"
              />

              <GosKpiCard
                tint={{ bg: 'var(--gos-amber-bg)', fg: 'var(--gos-amber-fg)' }}
                iconPath="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
                label="Avg. Deal Size"
                value="$68.2K"
                spark={[60, 58, 62, 59, 57, 61, 58, 55]}
                delta={{ up: false, text: '-1.4%' }}
                sparkId="gosSpark4"
              />
            </div>
          </div>
          <div className="gos-graphs">
            <div className="gos-dash-2col">
              <div className="card gos-card">
                <div className="gos-card-head">
                  <div className="gos-card-title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM12 12h.01" />
                    </svg>
                    Pipeline Overview
                  </div>
                  <Link to={`/opportunity`} className="gos-link">
                    View pipeline
                  </Link>
                </div>
                <div className="gos-card-pad">
                  {pipelineStages.length > 0 ? (
                    <GosPipeline stages={pipelineStages} />
                  ) : (
                    <div className="gos-graph-empty">No pipeline data</div>
                  )}
                </div>
              </div>

              <div className="card gos-card">
                <div className="gos-card-head">
                  <div className="gos-card-title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2a10 10 0 1 0 10 10H12V2Z" />
                      <path d="M12 2a10 10 0 0 1 10 10" />
                    </svg>
                    Lead Sources
                  </div>
                </div>
                <div className="gos-card-pad gos-donut-row">
                  <GosDonut data={DUMMY_SOURCE_MIX} size={160} />
                  <div className="gos-donut-legend">
                    {DUMMY_SOURCE_MIX.map((s, i) => (
                      <div className="gos-donut-leg" key={i}>
                        <span className="gos-donut-name">
                          <span className="gos-donut-dot" style={{ background: s.color }}></span>
                          {s.name}
                        </span>
                        <span className="gos-donut-val">{s.value}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {false && (
            <div className="gos-dash-2col">
              {/* Revenue Closed card commented out for now (no real data source yet)
              <div className="card gos-card">
                <div className="gos-card-head">
                  <div className="gos-card-title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 17l6-6 4 4 8-8M21 7v5h-5" />
                    </svg>
                    Revenue Closed
                  </div>
                </div>
                <div className="gos-card-pad">
                  <GosAreaChart data={DUMMY_REVENUE} />
                </div>
              </div>
              */}

              <div className="card gos-card">
                <div className="gos-card-head">
                  <div className="gos-card-title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 6v6l4 2" />
                    </svg>
                    Recent Activity
                  </div>
                </div>
                <div className="gos-card-pad gos-activity">
                  {DUMMY_ACTIVITY.map((a, i) => (
                    <div className="gos-activity-item" key={i}>
                      <div className="gos-activity-av" style={{ background: a.color }}>
                        {a.init}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="gos-activity-text">
                          <strong>{a.who}</strong> {a.action}{' '}
                          <span className="gos-activity-target">{a.target}</span>
                          {a.detail ? ' ' + a.detail : ''}
                        </div>
                        <div className="gos-activity-time">{a.time}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            )}
          </div>

          <div className="gos-grid2">
            <GosRecentTickets
              weekList={weekTicketList}
              todayList={todayTicketList}
              tab={ticketTab}
              setTab={setTicketTab}
              loading={isShowsLoader}
            />
            <GosRecentOpps opps={opportunityList} loading={isShowsLoader} />
          </div>
          </>
          )}

          <div className="row" style={{ margin: 0 }}>
            {false && AttendanceFlag && (
              <div className="col-xl-3 col-lg-4 col-md-12">
                <div className="card shadow-sm h-100" style={{ borderRadius: "12px" }}>
                  <div className="card-body p-3">
                    <div className="text-center">
                      <div className="profile-img-container mx-auto mb-2">
                        <img
                          src='/assets/images/profile.png'
                          alt="John Smith"
                        />
                        <div className="status-dot"></div>
                      </div>
                      <div className="fw-bold">
                        {userInfo?.empId ? (`${userInfo?.empId} -`) : null} {userInfo?.display_name}
                      </div>
                      <br></br>
                      <div className="text-muted small" style={{ textAlign: "center" }}> {userInfo?.position}</div>

                      <button
                        className={buttonLabel === "Checkin" ? "btn btn-checkIn w-100" : "btn btn-danger w-100"}
                        onClick={handleCheckinCheckout}
                        disabled={loading}
                      >
                        {loading ? 'Please wait...' : buttonLabel}
                      </button>
                    </div>

                    <hr />

                    <div className="small">
                      <p>
                        <strong>Email:</strong>
                        {/* <br /> */}
                        {userInfo?.userName}
                      </p>
                      <p>
                        <strong>Location:</strong>
                        {/* <br /> */}
                        {userInfo?.location}
                      </p>
                      <p>
                        <strong>Department:</strong>
                        {/* <br /> */}
                        {userInfo?.businessUnit}
                      </p>
                      <p>
                        <strong>Shift:</strong>
                        {/* <br /> */}
                        General - 09:30 AM - 06:30 PM
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="col-12" style={{ padding: 0 }}>
              {false && (
              <>
              <div className="grid-cards" style={{ margin: "0" }}>
                <div className="cardboxes">
                  <div className="card">
                    <div className="card-header">
                      <div className="title-left-hd">
                        <div className="attachment-background"></div>
                        <div className="title-heading">My Tickets</div>
                      </div>
                      <div className=" justify-content-center dsk">
                        <ul className="nav nav-tabs">
                          <li className="nav-item">
                            <a className="nav-link active" href="#tab1" data-bs-toggle="tab">
                              This Week ({weekTicketList.length})
                            </a>
                          </li>
                          <li className="nav-item">
                            <a className="nav-link" href="#tab2" data-bs-toggle="tab">
                              Today ({todayTicketList.length})
                            </a>
                          </li>
                        </ul>
                      </div>
                      <Link className="next-page-btn" to={`/tickets`}>
                        <svg
                          id="Component_28_1"
                          data-name="Component 28 – 1"
                          xmlns="http://www.w3.org/2000/svg"
                          width="32"
                          height="32"
                          viewBox="0 0 32 32"
                        >
                          <circle
                            id="Ellipse_45"
                            data-name="Ellipse 45"
                            cx="16"
                            cy="16"
                            r="16"
                            fill="none"
                          ></circle>
                          <path
                            id="Icon_ionic-ios-arrow-round-forward"
                            data-name="Icon ionic-ios-arrow-round-forward"
                            d="M18.869,11.472a.782.782,0,0,0-.006,1.1L22.5,16.22H8.647a.778.778,0,0,0,0,1.557H22.5l-3.641,3.647a.788.788,0,0,0,.006,1.1.775.775,0,0,0,1.1-.006l4.934-4.97h0a.874.874,0,0,0,.162-.246.743.743,0,0,0,.06-.3.781.781,0,0,0-.222-.545l-4.934-4.97A.763.763,0,0,0,18.869,11.472Z"
                            fill="#000"
                          ></path>
                        </svg>
                      </Link>
                    </div>

                    <div className="card-body">
                      <div className=" justify-content-center mob">
                        <ul className="nav nav-tabs">
                          <li className="nav-item">
                            <a className="nav-link active" href="#tab1" data-bs-toggle="tab">
                              This Week ({weekTicketList.length})
                            </a>
                          </li>
                          <li className="nav-item">
                            <a className="nav-link" href="#tab2" data-bs-toggle="tab">
                              Today ({todayTicketList.length})
                            </a>
                          </li>
                        </ul>
                      </div>
                      <div className="tab-content">
                        <div className="tab-pane fade show active" id="tab1">
                          <div className="card-text dsk">
                            <div className="home_grid">
                              <table className="table custom-table mb-0">
                                <tbody>
                                  {isShowsLoader ? ( // Check if loading is true
                                    <tr>
                                      <td colSpan="4" style={{ textAlign: 'center' }}>
                                        <div className="loader"></div>{' '}
                                        {/* Add your loader here */}
                                        Loading...
                                      </td>
                                    </tr>
                                  ) : weekTicketList && weekTicketList.length > 0 ? (
                                    weekTicketList.map((ticket, index) => (
                                      <tr key={index}>
                                        <td>
                                          <p>{ticket.description}</p>
                                          <Link>{ticket.subject}</Link>
                                        </td>
                                        <td>
                                          <div className="active">
                                            <span className="dotsactive"></span>
                                            <Link>{ticket.status}</Link>
                                          </div>
                                        </td>
                                        <td>
                                          <div className="highpriority">
                                            <Link>{ticket.priority}</Link>
                                          </div>
                                        </td>
                                        <td>
                                          <Link>{DateFormatter(ticket.dueDate)}</Link>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan="4" style={{ textAlign: 'center' }}>
                                        No tickets available
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          <div className="card-text mob">
                            <div className="myTicketBlock">
                              {isShowsLoader ? ( // Check if loading is true
                                <div>
                                  <div style={{ textAlign: 'center' }}>
                                    <div className="loader"></div> {/* Add your loader here */}
                                    Loading...
                                  </div>
                                </div>
                              ) : weekTicketList && weekTicketList.length > 0 ? (
                                weekTicketList.map((ticket, index) => (
                                  <div key={index} className="myTicketOuterBlock">
                                    <div className="myTicketInner">
                                      <p>{ticket.description}</p>
                                      <Link className="subjectName">{ticket.subject}</Link>
                                    </div>
                                    <div className="myTicketInner statusBlock">
                                      <div className="active">
                                        <span className="dotsactive"></span>
                                        <Link>{ticket.status}</Link>
                                      </div>
                                      <div className="highpriority">
                                        <Link>{ticket.priority}</Link>
                                      </div>
                                    </div>

                                    <div className="myTicketInner">
                                      <Link className="dateBlk">
                                        <i className="bi bi-calendar"></i>{' '}
                                        {DateFormatter(ticket.dueDate)}
                                      </Link>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div>
                                  <div style={{ textAlign: 'center' }}>No tickets available</div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="tab-pane fade" id="tab2">
                          <div className="card-text dsk">
                            <div className="home_grid">
                              <table className="table table-striped">
                                <tbody>
                                  {isShowsLoader ? ( // Check if loading is true
                                    <tr>
                                      <td colSpan="4" style={{ textAlign: 'center' }}>
                                        <div className="loader"></div>{' '}
                                        {/* Add your loader here */}
                                        Loading...
                                      </td>
                                    </tr>
                                  ) : todayTicketList && todayTicketList.length > 0 ? (
                                    todayTicketList.map((ticket, index) => (
                                      <tr key={index}>
                                        <td>
                                          <p>{ticket.description}</p>
                                          <Link>{ticket.subject}</Link>
                                        </td>
                                        <td>
                                          <div className="active">
                                            <span className="dotsactive"></span>
                                            <Link>{ticket.status}</Link>
                                          </div>
                                        </td>
                                        <td>
                                          <div className="highpriority">
                                            <Link>{ticket.priority}</Link>
                                          </div>
                                        </td>
                                        <td>
                                          <Link>{DateFormatter(ticket.dueDate)}</Link>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan="4">
                                        <div
                                          className="noDataBlock"
                                          style={{ height: 'calc(100vh - 384px)' }}
                                        >
                                          <div className="NodataContent">
                                            <img src="../assets/images/empty-box.png" alt="" />
                                            <p>No Tickets Available.</p>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          <div className="card-text mob">
                            <div className="myTicketBlock">
                              {isShowsLoader ? ( // Check if loading is true
                                <div>
                                  <div style={{ textAlign: 'center' }}>
                                    <div className="loader"></div> {/* Add your loader here */}
                                    Loading...
                                  </div>
                                </div>
                              ) : todayTicketList && todayTicketList.length > 0 ? (
                                todayTicketList.map((ticket, index) => (
                                  <div key={index} className="myTicketOuterBlock">
                                    <div className="myTicketInner">
                                      <p>{ticket.description}</p>
                                      <Link className="subjectName">{ticket.subject}</Link>
                                    </div>
                                    <div className="myTicketInner statusBlock">
                                      <div className="active">
                                        <span className="dotsactive"></span>
                                        <Link>{ticket.status}</Link>
                                      </div>
                                      <div className="highpriority">
                                        <Link>{ticket.priority}</Link>
                                      </div>
                                    </div>

                                    <div className="myTicketInner">
                                      <Link className="dateBlk">
                                        <i className="bi bi-calendar"></i>{' '}
                                        {DateFormatter(ticket.dueDate)}
                                      </Link>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div
                                  className="noDataBlock"
                                  style={{ height: 'calc(100vh - 384px)' }}
                                >
                                  <div className="NodataContent">
                                    <img src="../assets/images/empty-box.png" alt="" />
                                    <p>No Tickets Available.</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="cardboxes graphCard">
                  <div className="card">
                    <div className="card-header">
                      <div className="title-left-hd">
                        <div className="attachment-background3"></div>
                        <div className="title-heading">Opportunity by Stages</div>
                      </div>
                      <Link className="next-page-btn" to={`/opportunity`}>
                        <svg
                          id="Component_28_1"
                          data-name="Component 28 – 1"
                          xmlns="http://www.w3.org/2000/svg"
                          width="32"
                          height="32"
                          viewBox="0 0 32 32"
                        >
                          <circle
                            id="Ellipse_45"
                            data-name="Ellipse 45"
                            cx="16"
                            cy="16"
                            r="16"
                            fill="none"
                          ></circle>
                          <path
                            id="Icon_ionic-ios-arrow-round-forward"
                            data-name="Icon ionic-ios-arrow-round-forward"
                            d="M18.869,11.472a.782.782,0,0,0-.006,1.1L22.5,16.22H8.647a.778.778,0,0,0,0,1.557H22.5l-3.641,3.647a.788.788,0,0,0,.006,1.1.775.775,0,0,0,1.1-.006l4.934-4.97h0a.874.874,0,0,0,.162-.246.743.743,0,0,0,.06-.3.781.781,0,0,0-.222-.545l-4.934-4.97A.763.763,0,0,0,18.869,11.472Z"
                            fill="#000"
                          ></path>
                        </svg>
                      </Link>
                    </div>

                    <div className="card-body">
                      <div
                        className="tab-content graphContent"
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        {isShowsLoader ? ( // Check if loading is true
                          <table className="table ">
                            <tbody>
                              <tr>
                                <td colSpan="4" style={{ textAlign: 'center' }}>
                                  <div className="loader"></div> {/* Add your loader here */}
                                  Loading...
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        ) : (
                          pipelineData.length > 0 && (
                            <PipelineDealsByStage apiData={pipelineData} />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid-cards" style={{ gridTemplateColumns: 'repeat(1, 100%)', margin: "0" }}>
                <div className="cardboxes" style={{ marginBottom: "0" }}>
                  <div className="card">
                    <div className="card-header">
                      <div className="title-left-hd">
                        <div className="attachment-background2"></div>
                        <div className="title-heading">Recent Open Opportunities</div>
                      </div>

                      <Link className="next-page-btn" to={`/opportunity`}>
                        <svg
                          id="Component_28_1"
                          data-name="Component 28 – 1"
                          xmlns="http://www.w3.org/2000/svg"
                          width="32"
                          height="32"
                          viewBox="0 0 32 32"
                        >
                          <circle
                            id="Ellipse_45"
                            data-name="Ellipse 45"
                            cx="16"
                            cy="16"
                            r="16"
                            fill="none"
                          ></circle>
                          <path
                            id="Icon_ionic-ios-arrow-round-forward"
                            data-name="Icon ionic-ios-arrow-round-forward"
                            d="M18.869,11.472a.782.782,0,0,0-.006,1.1L22.5,16.22H8.647a.778.778,0,0,0,0,1.557H22.5l-3.641,3.647a.788.788,0,0,0,.006,1.1.775.775,0,0,0,1.1-.006l4.934-4.97h0a.874.874,0,0,0,.162-.246.743.743,0,0,0,.06-.3.781.781,0,0,0-.222-.545l-4.934-4.97A.763.763,0,0,0,18.869,11.472Z"
                            fill="#000"
                          ></path>
                        </svg>
                      </Link>
                    </div>

                    <div className="card-body">
                      <div className="tab-content" style={{ height: 'calc(100vh - 78vh)' }}>
                        <div className="tab-pane fade show active" id="tab1">
                          <div className="card-text dsk">
                            <div className="home_grid">
                              <table className="table table-striped custom-table ">
                                <thead>
                                  <tr>
                                    <th>Name</th>
                                    <th>Account Name</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {isShowsLoader ? (
                                    <tr>
                                      <td colSpan="4" style={{ textAlign: 'center' }}>
                                        <div className="loader"></div> Loading...
                                      </td>
                                    </tr>
                                  ) : opportunityList && opportunityList.length > 0 ? (
                                    opportunityList.map((opportunity, index) => (
                                      <tr key={index}>
                                        <td>
                                          <Link>{opportunity.name}</Link>
                                        </td>
                                        <td>
                                          <Link>{opportunity.accountName}</Link>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan="2">
                                        <div className="noDataBlock">
                                          <div className="NodataContent">
                                            <img src="../assets/images/empty-box.png" alt="" />
                                            <p>No Opportunities Available.</p>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          <div className="card-text mob">
                            <div className="recentOpp ">
                              {isShowsLoader ? (
                                <div style={{ textAlign: 'center' }}>
                                  <div className="loader"></div> Loading...
                                </div>
                              ) : opportunityList && opportunityList.length > 0 ? (
                                opportunityList.map((opportunity, index) => (
                                  <div key={index} className="recentOppBlock">
                                    <div className="recentOppName">
                                      <Link>{opportunity.name}</Link>
                                    </div>
                                    <div className="recentOppAccountName">
                                      <Link>{opportunity.accountName}</Link>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="noDataBlock">
                                  <div className="NodataContent">
                                    <img src="../assets/images/empty-box.png" alt="" />
                                    <p>No Opportunities Available.</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </>
              )}
            </div>
          </div>
          {/* </div> */}
        </div>
      </div>

      {setShowCheckoutConfirm && (
        <div>
          <ConfirmCheckOutModal
            show={showCheckoutConfirm}
            onConfirm={processAttendance}
            onCancel={() => setShowCheckoutConfirm(false)}
          />
        </div>
      )}


    </>

  );
};

export default HomeCRM;
